import React, { useState, useEffect } from "react";
import Select from "react-select";
import "./TopFilters.css";
import useApiFilters from "./useApiFilters";

/* ================= HELPERS ================= */

const toOptions = (arr = []) =>
  arr?.map((val) => ({ value: val, label: val }));

const formatGenderOptions = (arr = []) => {
  const genderMap = {
    F: "Female",
    M: "Male",
    O: "Others",
    FEMALE: "Female",
    MALE: "Male",
    OTHERS: "Others",
  };

  return arr
    ?.map((val) => {
      const upperVal = String(val).trim().toUpperCase();
      if (genderMap[upperVal]) {
        return { value: val, label: genderMap[upperVal] };
      }
      return null;
    })
    .filter(Boolean);
};

/* ================= MULTI SELECT ================= */

const Dropdown = ({
  label,
  value = [],
  options = [],
  onChange,
  disabled,
}) => (
  <div className="filter-item">
    <label>{label}</label>
    <Select
      isMulti
      isClearable
      isDisabled={disabled}
      closeMenuOnSelect={false}
      menuPortalTarget={document.body}
      options={options}
      value={options.filter((opt) =>
        value?.includes(opt.value)
      )}
      onChange={(selected) =>
        onChange(
          selected ? selected.map((s) => s.value) : []
        )
      }
      styles={{
        menuPortal: (base) => ({
          ...base,
          zIndex: 9999,
        }),
      }}
    />
  </div>
);

/* ================= MAIN COMPONENT ================= */

const TopFilters = ({ filters, onApply, onReset }) => {

  const [localFilters, setLocalFilters] = useState(filters);

  const [tempFromMonth, setTempFromMonth] = useState(filters?.fromMonth || "");
  const [tempToMonth, setTempToMonth] = useState(filters?.toMonth || "");

  const [dateError, setDateError] = useState("");

  const { options, loading, isFirstLoad } =
    useApiFilters(localFilters);

  useEffect(() => {
    setLocalFilters(filters);
    setTempFromMonth(filters.fromMonth || "");
    setTempToMonth(filters.toMonth || "");
  }, [filters]);

  const handleChange = (key, value) => {
    setLocalFilters((prev) => ({
      ...prev,
      [key]: value || []
    }));
  };

  /* ================= APPLY FILTERS ================= */

  const handleApply = () => {

    const from = localFilters.fromMonth;
    const to = localFilters.toMonth;

    // Only one date selected
    if ((from && !to) || (!from && to)) {
      setDateError("Please select both From Date and To Date");
      return;
    }

    setDateError("");
    onApply(localFilters);
  };

  /* ================= RESET ================= */

  const handleReset = () => {
    onReset();
    setDateError("");
  };

  if (isFirstLoad) {
    return (
      <div className="top-filters">
        <div className="filter-loader">
          Loading filters...
        </div>
      </div>
    );
  }

  return (
    <div className="top-filters">
      <div className="filters-wrapper">

        {loading && (
          <div className="filters-overlay">
            <div className="spinner"></div>
            Updating dynamic filters...
          </div>
        )}

        <div className={`filters-container ${loading ? "loading" : ""}`}>

          {/* ================= ROW 1 ================= */}

          <div className="filter-section">
            <h4>📍 Timeline & Region</h4>
            <div className="filter-row">

              <div className="filter-item">
                <label>From Month</label>
                <input
                  type="month"
                  disabled={loading}
                  value={tempFromMonth}
                  onChange={(e) =>
                    setTempFromMonth(e.target.value)
                  }
                  onBlur={() => {
                    handleChange("fromMonth", tempFromMonth);
                    setDateError("");
                  }}
                />
              </div>

              <div className="filter-item">
                <label>To Month</label>
                <input
                  type="month"
                  disabled={loading}
                  value={tempToMonth}
                  onChange={(e) =>
                    setTempToMonth(e.target.value)
                  }
                  onBlur={() => {
                    handleChange("toMonth", tempToMonth);
                  }}
                />
              </div>

              <Dropdown
                label="State"
                value={localFilters.regionState}
                options={toOptions(options.regionState || [])}
                onChange={(val) => handleChange("regionState", val)}
                disabled={loading}
              />

              <Dropdown
                label="District"
                value={localFilters.regionDistrict}
                options={toOptions(options.regionDistrict || [])}
                onChange={(val) => handleChange("regionDistrict", val)}
                disabled={loading}
              />

              <Dropdown
                label="Age"
                value={localFilters.tgAge}
                options={toOptions(options.tgAge || [])}
                onChange={(val) => handleChange("tgAge", val)}
                disabled={loading}
              />

            </div>
          </div>

          {/* ================= ROW 2 ================= */}

          <div className="filter-section">
            <h4>🏢 Client / Project</h4>
            <div className="filter-row">

              <Dropdown
                label="Project Name"
                value={localFilters.clientProjectName}
                options={toOptions(options.clientProjectName || [])}
                onChange={(val) =>
                  handleChange("clientProjectName", val)
                }
                disabled={loading}
              />

              <Dropdown
                label="PID"
                value={localFilters.clientProjectType}
                options={toOptions(options.clientProjectType || [])}
                onChange={(val) =>
                  handleChange("clientProjectType", val)
                }
                disabled={loading}
              />

              <Dropdown
                label="Service Type"
                value={localFilters.serviceType}
                options={toOptions(options.serviceType || [])}
                onChange={(val) =>
                  handleChange("serviceType", val)
                }
                disabled={loading}
              />

              <Dropdown
                label="Scheme Name"
                value={localFilters.schemeName}
                options={toOptions(options.schemeName || [])}
                onChange={(val) =>
                  handleChange("schemeName", val)
                }
                disabled={loading}
              />

              <Dropdown
                label="Income"
                value={localFilters.tgIncome}
                options={toOptions(options.tgIncome || [])}
                onChange={(val) =>
                  handleChange("tgIncome", val)
                }
                disabled={loading}
              />

            </div>
          </div>

          {/* ================= ROW 3 ================= */}

          <div className="filter-section">
            <h4>👥 Target Group</h4>
            <div className="filter-row">

              <Dropdown
                label="Gender"
                value={localFilters.tgGender}
                options={formatGenderOptions(options.tgGender || [])}
                onChange={(val) =>
                  handleChange("tgGender", val)
                }
                disabled={loading}
              />

              <Dropdown
                label="PWD"
                value={localFilters.tgPWD}
                options={toOptions(options.tgPWD || [])}
                onChange={(val) =>
                  handleChange("tgPWD", val)
                }
                disabled={loading}
              />

              <Dropdown
                label="Ration Card"
                value={localFilters.tgRationCard}
                options={toOptions(options.tgRationCard || [])}
                onChange={(val) =>
                  handleChange("tgRationCard", val)
                }
                disabled={loading}
              />

              <Dropdown
                label="Occupation"
                value={localFilters.tgOccupation}
                options={toOptions(options.tgOccupation || [])}
                onChange={(val) =>
                  handleChange("tgOccupation", val)
                }
                disabled={loading}
              />

<Dropdown
  label="Thematic Area"
  value={localFilters.thematicArea}
  options={toOptions(options.thematicArea || [])}
  onChange={(val) => handleChange("thematicArea", val)}
  disabled={loading}
/>
              {/* ACTIONS */}

              <div className="filter-actions-inline">

                {dateError && (
                  <div className="date-error">
                    {dateError}
                  </div>
                )}

                <div className="filter-buttons">

                  <button
                    className="apply-btn"
                    onClick={handleApply}
                    disabled={loading}
                  >
                    Apply Filters
                  </button>

                  <button
                    className="reset-btn"
                    onClick={handleReset}
                    disabled={loading}
                  >
                    Clear Filters
                  </button>

                </div>

              </div>

            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default TopFilters;